/** Pure helpers and shared types for the appointments page and its parts. */
import { formatClockTime, shopWallClockDate } from '../../../utils/shopTime';
import { Appointment, AppointmentStatus, TaskStatus } from '../../../types';

// Time slots available for selection
export const BUSINESS_HOURS = {
  start: 9, // 9:00 AM (updated from 8:00 AM)
  end: 20,  // 8:00 PM
};

// List of common US timezones for dropdown
export const timezoneOptions = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'America/Phoenix', label: 'Arizona (no DST)' },
];

// Update the time slot generation function to use the shop's time slot settings
export const generateTimeSlots = (timeSlotSize = 30, startHour = BUSINESS_HOURS.start, endHour = BUSINESS_HOURS.end) => {
  const slots = [];
  
  // Handle time slot generation with a more precise algorithm
  // This ensures 45-minute slots work correctly
  let currentHour = startHour;
  let currentMinute = 0;
  
  // Loop until we reach the end hour
  while (currentHour < endHour) {
    // Format the time slot
    const formattedHour = currentHour.toString().padStart(2, '0');
    const formattedMinute = currentMinute.toString().padStart(2, '0');
    
    // Add the slot
    slots.push(`${formattedHour}:${formattedMinute}`);
    
    // Increment by the time slot size
    currentMinute += timeSlotSize;
    
    // Handle overflow to the next hour
    if (currentMinute >= 60) {
      currentHour += Math.floor(currentMinute / 60);
      currentMinute = currentMinute % 60;
    }
  }
  
  // Add debugging
  console.log(`Generated ${slots.length} time slots with size ${timeSlotSize} minutes, from ${startHour}:00 to ${endHour}:00:`, slots);
  
  return slots;
};

// Update the DEFAULT_TIME_SLOTS to be empty, we'll generate them dynamically
export const DEFAULT_TIME_SLOTS: string[] = [];

/**
 * The appointment's time as read on its shop's wall clock (see
 * shopWallClockDate) — for display and grouping only.
 */
export const getAppointmentDate = (appointment: Appointment, timeZone?: string | null): Date | null => {
  if (appointment.appointmentDateTime) {
    return shopWallClockDate(appointment.appointmentDateTime, timeZone);
  }
  if (appointment.appointmentDate) {
    return shopWallClockDate(appointment.appointmentDate, timeZone);
  }
  if (appointment.appointmentDateObj instanceof Date && !isNaN(appointment.appointmentDateObj.getTime())) {
    return appointment.appointmentDateObj;
  }
  return null;
};



export const formatStatusLabel = (status: AppointmentStatus): string =>
  status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

export const isDeletableStatus = (status: AppointmentStatus): boolean =>
  [AppointmentStatus.PENDING, AppointmentStatus.CONFIRMED, AppointmentStatus.IN_PROGRESS].includes(status);

/** Reporting windows the list offers. */
export type HistoryWindow = '30d' | '90d' | '365d' | 'all';

export const HISTORY_WINDOWS: { key: HistoryWindow; label: string; days: number | null }[] = [
  { key: '30d', label: 'Last 30 days', days: 30 },
  { key: '90d', label: 'Last 90 days', days: 90 },
  { key: '365d', label: 'Last 12 months', days: 365 },
  { key: 'all', label: 'All time', days: null },
];

/**
 * `from`/`to` for a window, in epoch seconds.
 *
 * The upper bound reaches forward as well as back: this page is as much about
 * upcoming bookings as past ones, so a window that ended at "now" would hide
 * everything that has been booked.
 */
export function historyRange(window: HistoryWindow): { from?: number; to?: number } {
  const days = HISTORY_WINDOWS.find((entry) => entry.key === window)?.days;
  if (!days) return {};

  const now = Math.floor(Date.now() / 1000);
  const day = 86_400;
  return { from: now - days * day, to: now + 365 * day };
}

/** "14:30" or "14:30:00" → "2:30 PM". */
export const formatTimeForDisplay = formatClockTime;

/** How full a slot is against the shop's concurrent-booking limit. */
export type SlotStatus = 'available' | 'partial' | 'full';

/** The staff "Schedule New Booking" form. */
export interface NewBookingForm {
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  vehicleModel: string;
  vehicleYear: string;
  licensePlate: string;
  serviceDescription: string;
  comments: string;
  mechanicId: string;
}

/** The create/edit task form. */
export interface TaskFormState {
  title: string;
  description: string;
  status: TaskStatus;
  assignedToId: string;
}
